import { Carousel } from "@breuni-ui/carousel";
import { safeDefine } from "@breuni-ui/safe-define";
import { isElement } from "../utils/isElement";
import { isInBottomSheet, openDeeplink } from "@mobile-app/native-dialog";

export type OutfitClickDetails = {
  farbId: string;
  relativePath: string;
  index: number;
};
export type BestandteileTrackingData = {
  syan: string;
  farbId: string;
  targetUrl: string;
  position: number;
  isProductstyling: boolean;
  unavailable: boolean;
  currentlySoldOut: boolean;
};

export type BestandteilTrackingData = {
  syan: string;
  farbId: string;
  targetUrl: string;
  headlineDE: string;
  index: number;
  isProductstyling: boolean;
  unavailable: boolean;
  currentlySoldOut: boolean;
};

export default class KombiModulCarousel extends Carousel {
  private static openPDS(url: string): void {
    if (isInBottomSheet()) {
      openDeeplink(url);
    } else {
      window.open(url, "_self");
    }
  }

  public connectedCallback(): void {
    super.connectedCallback();
    const buttons = this.querySelectorAll('[data-id="open-bestandteile"]');
    buttons.forEach((button) => {
      button.addEventListener("click", (ev) => {
        this.dispatchOutfitClickEvent(ev);
      });
    });
    const anchors = this.querySelectorAll('[data-id="open-bestandteil"]');
    anchors.forEach((anchor) => {
      anchor.addEventListener("click", (ev) => {
        this.handleBestandteilClickEvent(ev);
      });
    });
  }

  public getBestandteileTrackingData(): BestandteileTrackingData[] {
    return Array.from(this.querySelectorAll("article.reco-bestandteil")).map(
      (el) => {
        const syan = el.getAttribute("data-syan") ?? "";
        const farbId = el.getAttribute("data-farb-id") ?? "";
        const targetUrl = (
          el
            .querySelector('a[data-id="open-bestandteil"]')
            ?.getAttribute("href") ?? ""
        ).replace(window.location.origin, "");
        const position = parseInt(
          el.getAttribute("data-slide-index") ?? "0",
          10,
        );
        const isProductstyling =
          el.getAttribute("data-productstyling-id") !== null;
        const unavailable =
          (el.getAttribute("data-unavailable") ?? "false") === "true";
        const currentlySoldOut =
          (el.getAttribute("data-currently-sold-out") ?? "false") === "true";
        return {
          syan,
          farbId,
          targetUrl,
          position,
          isProductstyling,
          currentlySoldOut,
          unavailable,
        };
      },
    );
  }

  private getBestandteilTrackingData(
    parentElement: HTMLElement,
    currentTarget: HTMLElement,
  ): BestandteilTrackingData | null {
    const {
      syan,
      farbId,
      headlineDE,
      href,
      rawIndex,
      productstylingId,
      unavailable,
      currentlySoldOut,
    } = this.getRawAttributes(parentElement, currentTarget);
    if (
      syan !== null &&
      farbId !== null &&
      headlineDE !== null &&
      href !== null &&
      rawIndex !== null &&
      unavailable !== null &&
      currentlySoldOut !== null
    ) {
      return {
        index: parseInt(rawIndex, 10),
        farbId,
        syan,
        targetUrl: href.replace(window.location.origin, ""),
        headlineDE,
        isProductstyling: productstylingId !== null,
        unavailable: unavailable === "true",
        currentlySoldOut: currentlySoldOut === "true",
      };
    }
    return null;
  }

  private getRawAttributes(
    parentElement: HTMLElement,
    currentTarget: HTMLElement,
  ): {
    syan: string | null;
    farbId: string | null;
    headlineDE: string | null;
    href: string | null;
    rawIndex: string | null;
    productstylingId: string | null;
    unavailable: string | null;
    currentlySoldOut: string | null;
  } {
    return {
      syan: parentElement.getAttribute("data-syan"),
      farbId: parentElement.getAttribute("data-farb-id"),
      headlineDE: this.getAttribute("data-headline-de"),
      href: currentTarget.getAttribute("href"),
      rawIndex: parentElement.getAttribute("data-slide-index"),
      productstylingId: parentElement.getAttribute("data-productstyling-id"),
      unavailable: parentElement.getAttribute("data-unavailable"),
      currentlySoldOut: parentElement.getAttribute("data-currently-sold-out"),
    };
  }

  private handleBestandteilClickEvent(ev: Event): void {
    ev.preventDefault();
    if (ev.currentTarget && isElement(ev.currentTarget)) {
      const { parentElement } = ev.currentTarget;
      if (parentElement) {
        const bestandteilTrackingData = this.getBestandteilTrackingData(
          parentElement,
          ev.currentTarget,
        );
        if (bestandteilTrackingData) {
          this.dispatchEvent(
            new CustomEvent<BestandteilTrackingData>("bestandteil:click", {
              detail: bestandteilTrackingData,
            }),
          );
        }
      }
      const href = ev.currentTarget.getAttribute("href");
      if (href !== null) {
        KombiModulCarousel.openPDS(href);
      }
    }
  }

  private dispatchOutfitClickEvent(ev: Event): void {
    if (ev.currentTarget && isElement(ev.currentTarget)) {
      const farbId = ev.currentTarget.getAttribute("data-farbid");
      const relativePath = ev.currentTarget.getAttribute("data-relative-path");
      const rawIndex =
        ev.currentTarget
          .closest("[data-slide-index]")
          ?.getAttribute("data-slide-index") ?? null;
      if (farbId !== null && relativePath !== null && rawIndex !== null) {
        const index = parseInt(rawIndex, 10);
        if (!Number.isNaN(index)) {
          this.dispatchEvent(
            new CustomEvent<OutfitClickDetails>("outfit:click", {
              detail: { farbId, relativePath, index },
            }),
          );
        }
      }
    }
  }
}

safeDefine("reco-kombi-modul-carousel", KombiModulCarousel);
